/**
 * Corroboration engine — ARCHITECTURE.md section 7.2.
 *
 * Computes NATO credibility ratings by cross-referencing entities across
 * independent sources. Updates entity corroboration data in-place and
 * persists via the entity store.
 *
 * Corroboration hierarchy:
 *   Strong: Identifier match across independent sources → credibility 1-2
 *   Moderate: Name + attributes match across sources → credibility 2-3
 *   Weak: Fuzzy name match → credibility 3-4, flagged for review
 *   Single source: No corroboration → credibility 5-6
 */

import type {
  Entity,
  NATOCredibility,
  NATORating,
  Corroboration,
} from '@/intelligence/entities/types';
import {
  getIdentifiers,
  isValidIdentifier,
  normalizeName,
} from '@/intelligence/entities/matching-utils';
import type { DeskConfig, SourceReliability } from '@/config/desk-config';
import {
  findCanonicalByIdentifier,
  findCanonicalByName,
  createCanonicalEntity,
  linkEntityToCanonical,
} from '@/intelligence/entities/canonical-store';
import { createProposal } from './staging-store';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Strength of corroboration between two entity mentions. */
export type CorroborationStrength = 'strong' | 'moderate' | 'weak' | 'none';

/** Result of comparing two entities for corroboration. */
export interface CorroborationMatch {
  entityA: Entity;
  entityB: Entity;
  strength: CorroborationStrength;
  reason: string;
  /** If weak, this match needs analyst review before merge. */
  requiresReview: boolean;
}

/** Result of running corroboration on a full entity set. */
export interface CorroborationResult {
  /** Entities with updated corroboration data. */
  entities: Entity[];
  /** Weak matches requiring analyst review (never auto-merged). */
  reviewRequired: CorroborationMatch[];
  /** Stats for logging. */
  stats: {
    totalEntities: number;
    corroborated: number;
    singleSource: number;
    weakMatches: number;
  };
}

// ---------------------------------------------------------------------------
// Corroboration scoring — ARCHITECTURE.md section 7.2
// ---------------------------------------------------------------------------

/**
 * Compute NATO credibility rating from corroboration count and strength.
 *
 * Strong: identifier match across sources → 1-2
 * Moderate: name + attributes → 2-3
 * Weak: fuzzy → 3-4
 * Single source: 5-6 (unless source reliability is A, then 4)
 */
function computeCredibility(
  sourceCount: number,
  bestStrength: CorroborationStrength,
  bestSourceReliability: SourceReliability,
): NATOCredibility {
  if (sourceCount >= 2) {
    switch (bestStrength) {
      case 'strong':
        return sourceCount >= 3 ? 1 : 2;
      case 'moderate':
        return sourceCount >= 3 ? 2 : 3;
      case 'weak':
        return sourceCount >= 3 ? 3 : 4;
      case 'none':
        return 5;
    }
  }

  // Single source
  if (bestSourceReliability === 'A') return 4;
  return sourceCount === 0 ? 6 : 5;
}

/**
 * Combine reliability (from desk config) and credibility (from corroboration)
 * into a NATO rating string like "B2".
 */
export function computeNATORating(
  reliability: SourceReliability,
  credibility: NATOCredibility,
): NATORating {
  return `${reliability}${credibility}` as NATORating;
}

// ---------------------------------------------------------------------------
// Entity matching — identifier, name, fuzzy
// ---------------------------------------------------------------------------

/**
 * Check if two entities share a matching identifier (MMSI, IMO, ICAO24, etc.).
 * Validates format before comparing.
 */
function hasIdentifierMatch(a: Entity, b: Entity): boolean {
  const idsA = getIdentifiers(a);
  const idsB = getIdentifiers(b);
  if (idsA.length === 0 || idsB.length === 0) return false;

  for (const idA of idsA) {
    for (const idB of idsB) {
      if (idA.type === idB.type && idA.value === idB.value) {
        if (!isValidIdentifier(idA)) continue;
        return true;
      }
    }
  }
  return false;
}

/** Check if two entities have a name + type + context match. */
function hasNameMatch(a: Entity, b: Entity): boolean {
  if (a.type !== b.type) return false;

  const nameA = normalizeName(a.name);
  const nameB = normalizeName(b.name);

  // Exact name match
  if (nameA === nameB) return true;

  // Check aliases
  const aliasesA = a.aliases.map(normalizeName);
  const aliasesB = b.aliases.map(normalizeName);

  if (aliasesA.includes(nameB) || aliasesB.includes(nameA)) return true;

  // Check cross-alias match
  for (const alias of aliasesA) {
    if (aliasesB.includes(alias)) return true;
  }

  return false;
}

/**
 * Check for fuzzy name match.
 * Uses simple heuristics: substring containment for multi-word names.
 * Returns true only if both entities are the same type.
 */
function hasFuzzyMatch(a: Entity, b: Entity): boolean {
  if (a.type !== b.type) return false;

  const nameA = normalizeName(a.name);
  const nameB = normalizeName(b.name);

  // Must be at least 3 characters and share significant overlap
  if (nameA.length < 3 || nameB.length < 3) return false;

  // One contains the other (e.g., "RFS Serpukhov" contains "Serpukhov")
  if (nameA.includes(nameB) || nameB.includes(nameA)) return true;

  // Word overlap: if both names share 2+ words of 3+ chars, consider it fuzzy
  const wordsA = nameA.split(/\s+/).filter(w => w.length >= 3);
  const wordsB = new Set(nameB.split(/\s+/).filter(w => w.length >= 3));
  let sharedWords = 0;
  for (const w of wordsA) {
    if (wordsB.has(w)) sharedWords++;
  }
  if (sharedWords >= 2) return true;

  return false;
}

/**
 * Determine corroboration strength between two entities.
 * Entities must come from independent sources (not citing each other).
 */
export function assessCorroboration(a: Entity, b: Entity): CorroborationStrength {
  // Same entity ID — not corroboration
  if (a.id === b.id) return 'none';

  // Must be from different sources to count as corroboration
  if (!areFromIndependentSources(a, b)) return 'none';

  // Strong: identifier match
  if (hasIdentifierMatch(a, b)) return 'strong';

  // Moderate: name + type match
  if (hasNameMatch(a, b)) return 'moderate';

  // Weak: fuzzy match (same type, overlapping names)
  if (hasFuzzyMatch(a, b)) return 'weak';

  return 'none';
}

/**
 * Check if two entities come from independent sources.
 * They are independent if their provenance source URLs differ.
 */
function areFromIndependentSources(a: Entity, b: Entity): boolean {
  const sourceA = a.provenance.sourceUrl;
  const sourceB = b.provenance.sourceUrl;

  // If either has no source URL, we can't verify independence
  if (!sourceA || !sourceB) return false;

  // Different source URLs = independent
  return sourceA !== sourceB;
}

// ---------------------------------------------------------------------------
// Source reliability lookup
// ---------------------------------------------------------------------------

/**
 * Look up source reliability from desk config.
 * Matches by checking if the entity's provenance source URL contains
 * the desk source's URL or endpoint domain.
 */
function getSourceReliability(entity: Entity, deskConfig: DeskConfig): SourceReliability {
  const sourceUrl = entity.provenance.sourceUrl;
  if (!sourceUrl) return 'F';

  for (const source of deskConfig.sources) {
    const sourceRef = source.url || source.endpoint || '';
    if (!sourceRef) continue;

    // Extract domain for matching
    const refDomain = extractDomain(sourceRef);
    const entityDomain = extractDomain(sourceUrl);

    if (refDomain && entityDomain && refDomain === entityDomain) {
      return source.reliability;
    }
  }

  // No match in desk config
  return 'F';
}

/** Get the best (highest) reliability among a set of desk sources. */
function getBestReliability(sources: string[], deskConfig: DeskConfig): SourceReliability {
  const reliabilityOrder: SourceReliability[] = ['A', 'B', 'C', 'D', 'E', 'F'];
  let bestIdx = reliabilityOrder.length - 1; // F

  for (const sourceUrl of sources) {
    for (const deskSource of deskConfig.sources) {
      const ref = deskSource.url || deskSource.endpoint || '';
      if (!ref) continue;

      const refDomain = extractDomain(ref);
      const srcDomain = extractDomain(sourceUrl);
      if (refDomain && srcDomain && refDomain === srcDomain) {
        const idx = reliabilityOrder.indexOf(deskSource.reliability);
        if (idx >= 0 && idx < bestIdx) bestIdx = idx;
      }
    }
  }

  return reliabilityOrder[bestIdx];
}

/** Extract domain from a URL for matching. */
function extractDomain(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    // Not a valid URL — try to extract domain-like string
    const match = url.match(/(?:https?:\/\/)?([^/\s:]+)/);
    return match ? match[1].replace(/^www\./, '') : '';
  }
}

// ---------------------------------------------------------------------------
// Main corroboration engine
// ---------------------------------------------------------------------------

/**
 * Run corroboration across all entities in an investigation.
 *
 * For each entity:
 * 1. Find all other entities from independent sources that match
 * 2. Determine corroboration strength (strong/moderate/weak/none)
 * 3. Count independent corroborating sources
 * 4. Compute NATO credibility rating
 * 5. Combine with source reliability for full NATO rating
 *
 * IMPORTANT: Weak/fuzzy matches are NEVER auto-merged (section 3.5).
 * They are flagged for analyst review.
 *
 * @param entities All entities in the investigation
 * @param deskConfig Active desk config (for source reliability ratings)
 * @returns Updated entities with corroboration data + review items
 */
export async function runCorroboration(
  entities: Entity[],
  deskConfig: DeskConfig,
  investigationId?: string,
): Promise<CorroborationResult> {
  const reviewRequired: CorroborationMatch[] = [];

  // Group entities by type for efficient matching
  const byType = new Map<string, Entity[]>();
  for (const entity of entities) {
    const group = byType.get(entity.type) || [];
    group.push(entity);
    byType.set(entity.type, group);
  }

  // For each entity, find corroborating entities
  const corroborationMap = new Map<string, {
    sources: Set<string>;
    bestStrength: CorroborationStrength;
    matchedEntities: Set<string>;
  }>();

  for (const entity of entities) {
    corroborationMap.set(entity.id, {
      sources: new Set(entity.provenance.sourceUrl ? [entity.provenance.sourceUrl] : []),
      bestStrength: 'none',
      matchedEntities: new Set(),
    });
  }

  // Compare every pair within the same type
  for (const [_type, group] of byType) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const a = group[i];
        const b = group[j];
        const strength = assessCorroboration(a, b);

        if (strength === 'none') continue;

        const dataA = corroborationMap.get(a.id)!;
        const dataB = corroborationMap.get(b.id)!;

        // Track corroborating sources
        if (b.provenance.sourceUrl) dataA.sources.add(b.provenance.sourceUrl);
        if (a.provenance.sourceUrl) dataB.sources.add(a.provenance.sourceUrl);

        // Track best strength
        if (strengthRank(strength) > strengthRank(dataA.bestStrength)) {
          dataA.bestStrength = strength;
        }
        if (strengthRank(strength) > strengthRank(dataB.bestStrength)) {
          dataB.bestStrength = strength;
        }

        // Track matched entities
        dataA.matchedEntities.add(b.id);
        dataB.matchedEntities.add(a.id);

        // Flag weak matches for review — NEVER auto-merge (section 3.5)
        if (strength === 'weak') {
          reviewRequired.push({
            entityA: a,
            entityB: b,
            strength: 'weak',
            reason: `Fuzzy name match between "${a.name}" and "${b.name}" (same type: ${a.type})`,
            requiresReview: true,
          });
        }
      }
    }
  }

  // Also compare across all types for identifier matches only
  // (An MMSI could appear on an ASSET and a related INDICATOR)
  for (let i = 0; i < entities.length; i++) {
    for (let j = i + 1; j < entities.length; j++) {
      const a = entities[i];
      const b = entities[j];
      if (a.type === b.type) continue; // Already handled above

      if (hasIdentifierMatch(a, b) && areFromIndependentSources(a, b)) {
        const dataA = corroborationMap.get(a.id)!;
        const dataB = corroborationMap.get(b.id)!;

        if (b.provenance.sourceUrl) dataA.sources.add(b.provenance.sourceUrl);
        if (a.provenance.sourceUrl) dataB.sources.add(a.provenance.sourceUrl);

        if (strengthRank('strong') > strengthRank(dataA.bestStrength)) {
          dataA.bestStrength = 'strong';
        }
        if (strengthRank('strong') > strengthRank(dataB.bestStrength)) {
          dataB.bestStrength = 'strong';
        }

        dataA.matchedEntities.add(b.id);
        dataB.matchedEntities.add(a.id);
      }
    }
  }

  // Compute NATO ratings and update entities
  let corroboratedCount = 0;
  let singleSourceCount = 0;

  const updatedEntities = entities.map(entity => {
    const data = corroborationMap.get(entity.id)!;
    const sourceCount = data.sources.size;
    const allSources = Array.from(data.sources);

    const reliability = getSourceReliability(entity, deskConfig);
    const bestReliability = getBestReliability(allSources, deskConfig);

    // Use the best reliability from all corroborating sources
    const effectiveReliability = strengthRank(data.bestStrength) > 0
      ? bestReliability
      : reliability;

    const credibility = computeCredibility(sourceCount, data.bestStrength, effectiveReliability);
    const natoRating = computeNATORating(effectiveReliability, credibility);

    if (sourceCount >= 2) {
      corroboratedCount++;
    } else {
      singleSourceCount++;
    }

    const corroboration: Corroboration = {
      count: sourceCount,
      sources: allSources.map(url => extractDomain(url)),
      natoRating,
    };

    return {
      ...entity,
      corroboration,
    };
  });

  // Cross-investigation canonical index linking
  // Runs async but we await to ensure canonical index is consistent
  if (investigationId) {
    await linkEntitiesToCanonicalIndex(updatedEntities, investigationId);
  }

  return {
    entities: updatedEntities,
    reviewRequired,
    stats: {
      totalEntities: entities.length,
      corroborated: corroboratedCount,
      singleSource: singleSourceCount,
      weakMatches: reviewRequired.length,
    },
  };
}

/**
 * Link entities to the canonical index for cross-investigation visibility.
 *
 * - Strong identifier match → auto-link
 * - Exact name + type match → auto-link
 * - Fuzzy name match → staging proposal for analyst review
 * - No match → create new canonical entity
 */
async function linkEntitiesToCanonicalIndex(
  entities: Entity[],
  investigationId: string,
): Promise<void> {
  for (const entity of entities) {
    try {
      // Try identifier match first (MMSI, IMO, ICAO24, etc.)
      const identifiers = getIdentifiers(entity);
      let linked = false;

      for (const identifier of identifiers) {
        if (!isValidIdentifier(identifier)) continue;
        const canonical = await findCanonicalByIdentifier(identifier.type, identifier.value);
        if (canonical) {
          await linkEntityToCanonical(entity.id, canonical.canonicalId, investigationId, 0.95, 'identifier');
          linked = true;
          break;
        }
      }

      if (linked) continue;

      // Try exact name match
      const nameMatch = await findCanonicalByName(entity.name, entity.type);
      if (nameMatch) {
        // Check if it's exact or fuzzy
        const normalized = normalizeName(entity.name);
        const canonicalNormalized = normalizeName(nameMatch.name);

        if (normalized === canonicalNormalized) {
          // Exact match — auto-link
          await linkEntityToCanonical(entity.id, nameMatch.canonicalId, investigationId, 0.85, 'name_exact');
        } else {
          // Fuzzy match — create staging proposal for analyst review
          await createProposal({
            id: `prop_${crypto.randomUUID()}`,
            proposalType: 'cross_link',
            sourceInvestigationId: investigationId,
            targetInvestigationId: null,
            entityIds: [entity.id],
            summary: `Possible match: "${entity.name}" may be the same as canonical entity "${nameMatch.name}" (${entity.type})`,
            detail: {
              entityId: entity.id,
              entityName: entity.name,
              canonicalId: nameMatch.canonicalId,
              canonicalName: nameMatch.name,
              matchType: 'name_fuzzy',
            },
            confidence: 0.5,
            tier: 1,
            createdAt: new Date().toISOString(),
          });
        }
        continue;
      }

      // No match — create new canonical entity
      const canonicalId = `canon_${crypto.randomUUID()}`;
      const ts = new Date().toISOString();
      await createCanonicalEntity({
        canonicalId,
        name: entity.name,
        type: entity.type,
        firstSeenAt: ts,
        lastSeenAt: ts,
      });

      await linkEntityToCanonical(entity.id, canonicalId, investigationId, 1.0, 'new');

      // Also register any strong identifiers
      // (canonical-store handles addCanonicalIdentifier internally if needed)
    } catch (err) {
      // Non-fatal — canonical linking failure shouldn't break corroboration
      console.warn(`[corroboration] Failed to link entity ${entity.id} to canonical index:`, err);
    }
  }
}

/** Rank corroboration strength for comparison. */
function strengthRank(strength: CorroborationStrength): number {
  switch (strength) {
    case 'strong': return 3;
    case 'moderate': return 2;
    case 'weak': return 1;
    case 'none': return 0;
  }
}

/**
 * Lightweight weak match finder — returns entity pairs with fuzzy matches
 * without running the full corroboration engine. Used when opening the graph
 * to display merge UI for unresolved matches.
 */
export function findWeakMatches(entities: Entity[]): CorroborationMatch[] {
  const matches: CorroborationMatch[] = [];
  // Group by type for comparison
  const byType = new Map<string, Entity[]>();
  for (const e of entities) {
    const group = byType.get(e.type) || [];
    group.push(e);
    byType.set(e.type, group);
  }

  for (const group of byType.values()) {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const strength = assessCorroboration(group[i], group[j]);
        if (strength === 'weak') {
          matches.push({
            entityA: group[i],
            entityB: group[j],
            strength: 'weak',
            reason: `Fuzzy name match between "${group[i].name}" and "${group[j].name}" (same type: ${group[i].type})`,
            requiresReview: true,
          });
        }
      }
    }
  }
  return matches;
}
